import * as React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import LoginLogo from "../../../assets/img/brand/Logo.png";
import { useForm } from "react-hook-form";
import * as Mui from "@mui/material";
import { InputAdornment } from "@material-ui/core";
import {
  ArrowBackOutlined,
  Check,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import sessionHandling from "services/utils/notificationUtils";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { showToast } from "services/utils/Status";
import "../Login/Login.css";
import {
  tokens,
  headingSx,
  fieldLabelSx,
  inputRootSx,
  primaryButtonSx,
  secondaryButtonSx,
  stepPillSx,
} from "../Login/brandTokens";
import {
  AuthLayout,
  AuthLeftPanel,
  AuthRightPanel,
  AuthCard,
} from "../Login/AuthShell";
import {
  parseJsonSafely,
  errorMessageFor,
  BackendRequestError,
} from "../Login/authErrors";

const UPDATE_PASSWORD_ERROR = "Couldn't update your password. Please try again.";

const stepItems = [
  { number: 1, title: "Verify identity", subtitle: "Email or mobile" },
  { number: 2, title: "Enter OTP", subtitle: "Check inbox or SMS" },
  { number: 3, title: "Set new password", subtitle: "Choose something strong" },
];

export const NewPassword = () => {
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [showCPassword, setShowCPassword] = useState(false);
  const [verified, setVerified] = useState(false);
  const { domain } = useSelector((state) => state.tokenReducer);
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    watch,
    formState: { errors },
  } = useForm({ mode: "onBlur", reValidateMode: "onChange" });
  const passwordValue = watch("password", "");

  const getPasswordStrength = (value) => {
    if (!value) return 0;
    let score = 0;
    if (value.trim().length >= 8) score += 1;
    if (/[a-z]/.test(value)) score += 1;
    if (/[A-Z]/.test(value)) score += 1;
    if (/[0-9]/.test(value)) score += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(value)) score += 1;
    return Math.min(4, Math.ceil((score / 5) * 4));
  };

  const passwordStrength = getPasswordStrength(passwordValue);
  const strengthMeta = [
    { label: "Weak", color: "#DC4C64" },
    { label: "Fair", color: "#E8A33D" },
    { label: "Good", color: "#6FE0A8" },
    { label: "Strong", color: tokens.primaryDark },
  ][Math.max(0, passwordStrength - 1)];

  const validatePassword = (value) => {
    if (!value?.trim()) {
      return "Password is required";
    }
    if (value.trim().length < 8) {
      return "Use at least 8 characters";
    }
    if (!/[a-z]/.test(value)) {
      return "Include a lowercase letter";
    }
    if (!/[A-Z]/.test(value)) {
      return "Include an uppercase letter";
    }
    if (!/[0-9]/.test(value)) {
      return "Include a number";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      return "Include a symbol";
    }
    return true;
  };

  function getCSRFToken() {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrftoken="))
      ?.split("=")[1];
  }

  const onSubmit = async (data) => {
    const urlLink = localStorage.getItem("resetPasswordLink");
    try {
      await fetch(`${domain}csrf/`, { credentials: "include" });
      const response = await fetch(urlLink, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCSRFToken(),
        },
        body: JSON.stringify({
          password: data.password,
          confirmpassword: data.cpassword,
        }),
      });
      const res = await parseJsonSafely(response);
      if (response.ok) {
        setVerified(true);
        reset();
        setTimeout(function () {
          history.push("/auth/login");
        }, 10000);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new BackendRequestError(
          errorMessageFor(res, UPDATE_PASSWORD_ERROR),
        );
      }
    } catch (error) {
      console.error("[NewPassword] update password failed:", error);
      showToast(
        "error",
        error instanceof BackendRequestError
          ? error.message
          : UPDATE_PASSWORD_ERROR,
      );
    }
  };

  const backToResetPassword = () => {
    history.push("/auth/send/otp");
  };

  return (
    <React.Fragment>
      <AuthLayout>
        <AuthLeftPanel
          top={
            <Mui.Stack spacing={4}>
              <Mui.Box sx={{ maxWidth: 520 }}>
                <Mui.Typography
                  sx={{
                    fontSize: { md: 28, lg: 40, xl: 46 },
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Regain access in
                  <br />
                  three simple steps
                </Mui.Typography>
                <Mui.Typography
                  sx={{
                    mt: 2.5,
                    fontSize: { md: 15, lg: 17 },
                    lineHeight: 1.5,
                    color: "rgba(255,255,255,0.72)",
                    maxWidth: 460,
                  }}
                >
                  We will verify your identity and get you back in securely.
                </Mui.Typography>
              </Mui.Box>

              <Mui.Stack spacing={0} sx={{ maxWidth: 460 }}>
                {stepItems.map((item, index) => {
                  const isActive = !verified && item.number === 3;
                  const isCompleted = verified || item.number < 3;

                  return (
                    <Mui.Box
                      key={item.number}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2.5,
                        position: "relative",
                      }}
                    >
                      <Mui.Box sx={{ position: "relative", flexShrink: 0 }}>
                        {index < stepItems.length - 1 ? (
                          <Mui.Box
                            sx={{
                              position: "absolute",
                              top: 54,
                              left: "50%",
                              transform: "translateX(-50%)",
                              width: 0,
                              height: 24,
                              borderLeft: "2px dashed rgba(255,255,255,0.25)",
                            }}
                          />
                        ) : null}
                        <Mui.Box
                          sx={{
                            width: 54,
                            height: 54,
                            borderRadius: "50%",
                            border: isActive
                              ? `2px solid ${tokens.mint}`
                              : isCompleted
                                ? `2px solid ${tokens.mint}`
                                : "2px solid rgba(255,255,255,0.26)",
                            background: isActive
                              ? tokens.mint
                              : isCompleted
                                ? tokens.mintTint
                                : "rgba(255,255,255,0.08)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 17,
                            fontWeight: 600,
                            color: isActive
                              ? tokens.mintDark
                              : isCompleted
                                ? tokens.mint
                                : "rgba(255,255,255,0.95)",
                          }}
                        >
                          {isCompleted ? <Check fontSize="small" /> : item.number}
                        </Mui.Box>
                      </Mui.Box>
                      <Mui.Box sx={{ pt: 0.75, pb: 3 }}>
                        <Mui.Typography
                          sx={{
                            fontSize: 17,
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? "#fff" : "rgba(255,255,255,0.92)",
                          }}
                        >
                          {item.title}
                        </Mui.Typography>
                        <Mui.Typography
                          sx={{
                            mt: 0.5,
                            fontSize: 14.5,
                            color: "rgba(255,255,255,0.58)",
                          }}
                        >
                          {item.subtitle}
                        </Mui.Typography>
                      </Mui.Box>
                    </Mui.Box>
                  );
                })}
              </Mui.Stack>
            </Mui.Stack>
          }
        />

        <AuthRightPanel>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <AuthCard maxWidth={{ xs: "100%", sm: 520, md: 480, lg: 560, xl: 600 }}>
              <Mui.Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                {!verified ? (
                  <Mui.Button
                    variant="outlined"
                    onClick={backToResetPassword}
                    startIcon={<ArrowBackOutlined />}
                    sx={{
                      ...secondaryButtonSx,
                      justifyContent: "flex-start",
                      minHeight: 0,
                      height: 44,
                      px: 2,
                      fontSize: 14.5,
                    }}
                  >
                    Back
                  </Mui.Button>
                ) : (
                  <Mui.Box />
                )}
                <Mui.Box sx={stepPillSx}>
                  {verified ? "Done!" : "Step 3 of 3"}
                </Mui.Box>
              </Mui.Box>

              {verified ? (
                <Mui.Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Mui.Box
                    sx={{
                      width: 54,
                      height: 54,
                      borderRadius: "50%",
                      background: "#edf5ef",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    <Mui.Box
                      component="img"
                      src={LoginLogo}
                      alt="OrganFlow mark"
                      sx={{ width: 38, height: 38, objectFit: "contain" }}
                    />
                  </Mui.Box>
                  <Mui.Typography sx={{ ...headingSx, mb: 1.5 }}>
                    Password updated!
                  </Mui.Typography>
                  <Mui.Typography
                    sx={{
                      fontSize: 15,
                      lineHeight: 1.5,
                      color: "rgba(21,23,26,0.7)",
                      mb: 3.5,
                    }}
                  >
                    Your password has been changed successfully. Sign in with
                    your new credentials.
                  </Mui.Typography>
                  <Mui.Button
                    variant="outlined"
                    type="button"
                    size="large"
                    sx={{ ...secondaryButtonSx, minWidth: 220, height: 50 }}
                    onClick={() => history.push("/auth/login")}
                  >
                    Back to sign in
                  </Mui.Button>
                </Mui.Box>
              ) : (
                <React.Fragment>
                  <Mui.Typography sx={headingSx}>
                    Set a new password
                  </Mui.Typography>

                  <Mui.Typography
                    sx={{
                      mt: 1,
                      fontSize: 15,
                      lineHeight: 1.4,
                      color: "rgba(21,23,26,0.7)",
                    }}
                  >
                    Choose a strong password you have not used before.
                  </Mui.Typography>

                  <Mui.Box sx={{ mt: 3.5, width: "100%" }}>
                    <Mui.Typography sx={fieldLabelSx}>
                      New password
                    </Mui.Typography>
                    <Mui.TextField
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": inputRootSx,
                        "& .MuiOutlinedInput-input": {
                          py: 1.75,
                        },
                      }}
                      size="medium"
                      placeholder="Enter the password"
                      type={showPassword ? "text" : "password"}
                      {...register("password", {
                        validate: validatePassword,
                      })}
                      onKeyDown={(event) => {
                        if (event.code === "Space") {
                          event.preventDefault();
                        }
                      }}
                      error={Boolean(errors.password)}
                      helperText={
                        errors.password?.message ||
                        "Use 8+ characters with letters, numbers, and symbols."
                      }
                      FormHelperTextProps={{
                        sx: {
                          mt: 1.25,
                          ml: 0,
                          minHeight: 20,
                          fontSize: 13,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Mui.IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </Mui.IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Mui.Box>

                  <Mui.Box
                    sx={{
                      mt: 1.25,
                      width: "100%",
                      display: "flex",
                      gap: 0.75,
                    }}
                  >
                    {[0, 1, 2, 3].map((segment) => (
                      <Mui.Box
                        key={segment}
                        sx={{
                          flex: 1,
                          height: 4,
                          borderRadius: 999,
                          backgroundColor:
                            segment < passwordStrength
                              ? strengthMeta.color
                              : "rgba(21,23,26,0.12)",
                          transition: "background-color 0.2s ease",
                          "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                          },
                        }}
                      />
                    ))}
                  </Mui.Box>
                  {passwordValue ? (
                    <Mui.Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 13,
                        fontWeight: 600,
                        color: strengthMeta.color,
                      }}
                    >
                      {strengthMeta.label}
                    </Mui.Typography>
                  ) : null}

                  <Mui.Box sx={{ mt: 2.5, width: "100%" }}>
                    <Mui.Typography sx={fieldLabelSx}>
                      Confirm password
                    </Mui.Typography>
                    <Mui.TextField
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": inputRootSx,
                        "& .MuiOutlinedInput-input": {
                          py: 1.75,
                        },
                      }}
                      size="medium"
                      placeholder="Enter the password"
                      type={showCPassword ? "text" : "password"}
                      {...register("cpassword", {
                        required: "Confirm password is required",
                        validate: {
                          passwordValidation: (value) =>
                            value === getValues("password") ||
                            "Password not matched",
                        },
                      })}
                      onKeyDown={(event) => {
                        if (event.code === "Space") {
                          event.preventDefault();
                        }
                      }}
                      error={Boolean(errors.cpassword)}
                      helperText={errors.cpassword?.message || " "}
                      FormHelperTextProps={{
                        sx: {
                          mt: 1.25,
                          ml: 0,
                          minHeight: 20,
                          fontSize: 13,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Mui.IconButton
                              aria-label="toggle confirm password visibility"
                              onClick={() => setShowCPassword(!showCPassword)}
                              edge="end"
                            >
                              {showCPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </Mui.IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Mui.Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 13.5,
                        color: "rgba(21,23,26,0.56)",
                      }}
                    >
                      Use 8+ characters with letters, numbers, and symbols.
                    </Mui.Typography>
                  </Mui.Box>

                  <Mui.Box sx={{ mt: 3.5, width: "100%" }}>
                    <Mui.Button
                      variant="contained"
                      type="submit"
                      size="large"
                      sx={primaryButtonSx}
                    >
                      Update password
                    </Mui.Button>
                  </Mui.Box>
                </React.Fragment>
              )}
            </AuthCard>
          </form>
        </AuthRightPanel>
      </AuthLayout>
    </React.Fragment>
  );
};
